import _ from "lodash";
import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import { DSProvider, PageHeader } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { goBack } from "services/NavigationService";
import { goToRelativeRoute } from "modules/Services";
import { getPrintFile } from "modules/PrintThunks";
import { isWebview as getIsWebView } from "Utils/SentralHelpers";
import NoDataComponent from "UIComponents/NoDataComponent";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import TheatreModeV2 from "UIComponents/TheatreModeV2";

import OngoingReflectionCard from "../OngoingReflectionCard";
import SharedReportCard from "../SharedReportCard";
import classes from "./ParentProgressReportV2.scss";

const ParentProgressReportV2 = props => {
  const {
    publishedProgressReports,
    studentReflections,
    goToRelativeRoute,
    getPrintFile,
  } = props;

  const { t } = useI18n([]);
  const [reportPreviewData, setReportPreviewData] = useState(null);
  const progressReportsCount = _.size(publishedProgressReports);
  const hasOngoing = _.size(studentReflections) > 0;
  const hasShared = progressReportsCount > 0;

  const isReportPreviewLoading = _.get(reportPreviewData, "isLoading", false);
  const reportPreviewUrl = _.get(reportPreviewData, "url", null);
  const reportPreviewTitle = _.get(reportPreviewData, "title", "");

  const mediaAttachments = useMemo(
    () => ({
      attachments: [
        {
          type: "FILE",
          mimeType: "application/pdf",
          url: reportPreviewUrl,
          name: reportPreviewTitle,
        },
      ],
    }),
    [reportPreviewUrl, reportPreviewTitle]
  );

  const handleViewClick = useCallback(
    async ({
      studentProgressReportId,
      publishedProgressReportId,
      reportTitle,
      curriculumProgramType,
    }) => {
      if (!getIsWebView()) {
        goToRelativeRoute({ route: studentProgressReportId });
        return;
      }
      setReportPreviewData({ isLoading: true });
      const result = await getPrintFile({
        id: studentProgressReportId,
        type: "progressreport",
        isDownload: false,
        shouldOpenInWindow: false,
        name: reportTitle,
        metadata: {
          studentProgressReportId,
          curriculumProgramType,
        },
      });
      setReportPreviewData({
        isLoading: false,
        url: result,
        title: reportTitle,
      });
    },
    [goToRelativeRoute, getPrintFile]
  );

  const onClosePreview = () => {
    setReportPreviewData(null);
  };

  const handleDownloadClick = ({
    studentProgressReportId,
    publishedProgressReportId,
    reportTitle,
    curriculumProgramType,
  }) => {
    getPrintFile({
      id: studentProgressReportId,
      type: "progressreport",
      isDownload: true,
      name: reportTitle,
      metadata: {
        studentProgressReportId,
        curriculumProgramType,
      },
    });
  };

  return (
    <DSProvider dsVersion="2.0">
      <PageHeader>
        <PageHeader.Left
          title={t("progressReport:progress_report_plural")}
          onBackClick={goBack}
        />
      </PageHeader>
      <section
        className={classes.sectionsContainer}
        data-test-id="parentProgressReportV2-container"
      >
        {hasOngoing ? (
          <section
            className={classes.section}
            data-test-id="parentProgressReportV2-ongoingSection"
          >
            <div className={classNames("text-label", classes.sectionTitle)}>
              {t("progressReport:ongoing_reflection")}
            </div>
            <div className={classes.cardsList}>
              {_.map(studentReflections, reflection => {
                const studentProgressReportId = _.get(
                  reflection,
                  "studentProgressReportId",
                  ""
                );
                return (
                  <OngoingReflectionCard
                    key={studentProgressReportId}
                    studentProgressReportId={studentProgressReportId}
                    progressReportName={_.get(
                      reflection,
                      "progressReportName",
                      ""
                    )}
                    reflectionDeadline={_.get(
                      reflection,
                      "reflectionDeadline",
                      ""
                    )}
                    isReflectionEditable={_.get(
                      reflection,
                      "isReflectionEditable",
                      false
                    )}
                    academicTerms={_.get(reflection, "academicTerms", [])}
                    reflectionFeedback={_.get(
                      reflection,
                      "reflectionFeedback",
                      null
                    )}
                    onActionClick={() => {
                      goToRelativeRoute({
                        route: `student-reflection/${studentProgressReportId}`,
                      });
                    }}
                  />
                );
              })}
            </div>
          </section>
        ) : null}
        {hasShared ? (
          <section
            className={classes.section}
            data-test-id="parentProgressReportV2-sharedSection"
          >
            <div className={classNames("text-label", classes.sectionTitle)}>
              {t("progressReport:shared_reports")}
            </div>
            <div className={classes.cardsList}>
              {_.map(publishedProgressReports, report => {
                const studentProgressReportId = _.get(
                  report,
                  "studentProgressReport.id",
                  ""
                );
                const publishedProgressReportId = _.get(report, "id", "");
                const publishedAt = _.get(report, "publishedAt", "");
                const reportTitle =
                  _.get(report, "filteredNodes.prTitle.value", "") ||
                  _.get(
                    report,
                    "studentProgressReport.courseProgressReportTask.title",
                    ""
                  );
                const curriculumProgramType = _.get(
                  report,
                  "curriculumProgram.type",
                  CURRICULUM_TYPE_PYP
                );
                return (
                  <SharedReportCard
                    key={publishedProgressReportId}
                    reportId={publishedProgressReportId}
                    reportTitle={reportTitle}
                    publishedAt={publishedAt}
                    onViewClick={() =>
                      handleViewClick({
                        studentProgressReportId,
                        publishedProgressReportId,
                        reportTitle,
                        curriculumProgramType,
                      })
                    }
                    onDownloadClick={() =>
                      handleDownloadClick({
                        studentProgressReportId,
                        publishedProgressReportId,
                        reportTitle,
                        curriculumProgramType,
                      })
                    }
                  />
                );
              })}
            </div>
          </section>
        ) : null}
        {!hasOngoing && !hasShared ? (
          <div className={classes.noDataContainer}>
            <NoDataComponent
              emptyText={""}
              emptyHeaderText={t("progressReport:no_progress_reports")}
              isSearch={false}
            />
          </div>
        ) : null}
      </section>
      {isReportPreviewLoading ? <FullScreenLoader /> : null}
      {reportPreviewUrl ? (
        <TheatreModeV2
          mediaAttachments={mediaAttachments}
          activeAttachmentIndex={0}
          toggleTheatreMode={onClosePreview}
        />
      ) : null}
    </DSProvider>
  );
};

ParentProgressReportV2.propTypes = {
  publishedProgressReports: PropTypes.arrayOf(PropTypes.object),
  studentReflections: PropTypes.arrayOf(
    PropTypes.shape({
      studentProgressReportId: PropTypes.string,
      progressReportName: PropTypes.string,
      reflectionDeadline: PropTypes.string,
      isReflectionEditable: PropTypes.bool,
      academicTerms: PropTypes.array,
      reflectionFeedback: PropTypes.object,
    })
  ),
};

ParentProgressReportV2.defaultProps = {
  publishedProgressReports: [],
  studentReflections: [],
};

const mapActionCreators = {
  goToRelativeRoute,
  getPrintFile,
};

export default compose(connect(null, mapActionCreators))(
  ParentProgressReportV2
);
